import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api, { setUnauthorizedHandler } from '../api/client';
import { invalidateCrmLookups } from '../pages/crm/useCrmLookups';
import { TOKEN_KEY } from '../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyIdentity = useCallback((data) => {
    setUser(data.user);
    setPermissions(data.permissions || []);
    setIsSuperAdmin(!!data.is_super_admin);
  }, []);

  const clearSession = useCallback(() => {
    invalidateCrmLookups();
    setUser(null);
    setPermissions([]);
    setIsSuperAdmin(false);
  }, []);

  // An expired/revoked token anywhere ends the session; <ProtectedRoute> then redirects.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  // Restore session on first load if a token is present.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get('/me')
      .then((res) => applyIdentity(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, [applyIdentity]);

  const login = useCallback(
    async (email, password) => {
      const res = await api.post('/login', { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      applyIdentity(res.data);
    },
    [applyIdentity]
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/logout');
    } catch (_) {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    clearSession();
  }, [clearSession]);

  /**
   * The single gate the whole UI uses. Super admins pass everything.
   */
  const can = useCallback(
    (permission) => isSuperAdmin || permissions.includes(permission),
    [isSuperAdmin, permissions]
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      isSuperAdmin,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      can,
    }),
    [user, permissions, isSuperAdmin, loading, login, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
